import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { TYRES, pressureDelta } from "@/lib/format";

const DeltaBadge = ({ cold, hot, unit, testid }) => {
  const d = pressureDelta(cold, hot);
  if (d == null) return <span className="font-mono text-xs text-slate-600">—</span>;
  const cls = d > 0 ? "text-[#FFB800]" : d < 0 ? "text-[#007AFF]" : "text-slate-400";
  return (
    <span data-testid={testid} className={`font-mono font-bold text-sm ${cls}`}>
      {d > 0 ? "+" : ""}{d.toFixed(unit === "psi" ? 1 : 2)} {unit}
    </span>
  );
};

const numInput = "bg-[#090A0F] border-[#282D3D] focus-visible:ring-[#FF3B30] focus-visible:ring-offset-0 font-mono text-lg font-bold text-center rounded-sm h-11";

const GROUPS = {
  cold: { title: "cold_group", accent: "text-[#007AFF]", bar: "bg-[#007AFF]" },
  hot: { title: "hot_group", accent: "text-[#FF3B30]", bar: "bg-[#FF3B30]" },
};

const PressureGroup = ({ kind, tyres, unit, onChange, prefix }) => {
  const { t } = useT();
  const g = GROUPS[kind];
  const set = (pos) => (e) => onChange({ ...tyres, [pos]: { ...tyres[pos], [kind]: e.target.value === "" ? null : e.target.value } });
  return (
    <div data-testid={`${prefix}-group-${kind}`} className="bg-[#12141C] border border-[#282D3D] rounded-sm p-3 sm:p-4">
      <div className={`font-heading uppercase font-bold text-lg mb-3 flex items-center gap-2 ${g.accent}`}>
        <span className={`w-1 h-4 ${g.bar}`} />{t(g.title)} <span className="font-mono text-xs text-slate-500 normal-case">({unit})</span>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {TYRES.map((pos) => (
          <label key={pos} className="space-y-1">
            <span className="flex justify-between items-center">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{t(`tyre_${pos}`)}</span>
              {kind === "hot" && <DeltaBadge cold={tyres[pos].cold} hot={tyres[pos].hot} unit={unit} testid={`${prefix}-${pos}-delta`} />}
            </span>
            <Input data-testid={`${prefix}-${pos}-${kind}-input`} type="number" step="0.01" inputMode="decimal" value={tyres[pos][kind] ?? ""} onChange={set(pos)} className={numInput} />
          </label>
        ))}
      </div>
    </div>
  );
};

export const TyreWidget = ({ tyres, unit, onChange, prefix = "tyre" }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <PressureGroup kind="cold" prefix={prefix} tyres={tyres} unit={unit} onChange={onChange} />
    <PressureGroup kind="hot" prefix={prefix} tyres={tyres} unit={unit} onChange={onChange} />
  </div>
);

export const UnitToggle = ({ unit, onChange }) => {
  const { t } = useT();
  return (
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("unit")}</span>
        {["bar", "psi"].map((u) => (
          <button type="button" key={u} data-testid={`pressure-unit-${u}`} onClick={() => onChange(u)}
            className={`px-3 py-1 font-mono text-xs uppercase border rounded-sm transition-colors duration-200 ${unit === u ? "border-[#FF3B30] text-white bg-[#FF3B30]/10" : "border-[#282D3D] text-slate-500 hover:text-white"}`}>
            {u}
          </button>
        ))}
      </div>
  );
};
